// src/components/CommandPalette.tsx
// Global ⌘K command palette: quick navigation to pages, settings, products and orders.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  InputBase,
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  CircularProgress,
  Divider,
  alpha,
  Theme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import type { SvgIconComponent } from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { SECTIONS, SETTINGS_ITEMS } from './Sidebar';
import { useOrderNotifications } from '../context/OrderNotificationContext';

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

interface PaletteEntry {
  key: string;
  primary: string;
  secondary?: string;
  tag: string;
  icon: SvgIconComponent;
  path: string;
  searchText: string;
  /** Selecting this entry should clear the unread-orders badge. */
  resetsOrders?: boolean;
}

interface PaletteGroup {
  id: string;
  label: string;
  entries: PaletteEntry[];
  hiddenCount: number;
}

const GROUP_CAP = 6;

const PAGE_ENTRIES: PaletteEntry[] = SECTIONS.flatMap((section) => section.items).map(
  (item) => ({
    key: `page:${item.path}`,
    primary: item.label,
    secondary: item.path,
    tag: 'Page',
    icon: item.icon,
    path: item.path,
    searchText: item.label.toLowerCase(),
    resetsOrders: item.ordersBadge,
  })
);

const SETTINGS_ENTRIES: PaletteEntry[] = SETTINGS_ITEMS.map((item) => ({
  key: `settings:${item.path}`,
  primary: item.label,
  secondary: item.path,
  tag: 'Settings',
  icon: item.icon,
  path: item.path,
  searchText: item.label.toLowerCase(),
}));

const Kbd: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    component="kbd"
    sx={{
      px: 0.6,
      py: 0.1,
      borderRadius: 0.75,
      border: '1px solid',
      borderColor: 'divider',
      bgcolor: 'action.hover',
      fontFamily: 'inherit',
      fontSize: '0.6875rem',
      lineHeight: 1.6,
      color: 'text.secondary',
    }}
  >
    {children}
  </Box>
);

const CommandPalette: React.FC<CommandPaletteProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const { resetUnread } = useOrderNotifications();

  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [products, setProducts] = useState<PaletteEntry[]>([]);
  const [orders, setOrders] = useState<PaletteEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const fetchStartedRef = useRef(false);
  const listRef = useRef<HTMLDivElement | null>(null);

  // Fetch products + recent orders ONCE, on the first open; cache for the session.
  useEffect(() => {
    if (!open || fetchStartedRef.current) return;
    fetchStartedRef.current = true;
    setLoading(true);

    (async () => {
      const [productsResult, ordersResult] = await Promise.allSettled([
        import('../api/superAdmin').then((m) => m.fetchProducts()),
        import('../api/orderApi').then((m) => m.fetchOrders({ limit: 100 })),
      ]);

      if (productsResult.status === 'fulfilled') {
        const raw = productsResult.value;
        const list: any[] = Array.isArray(raw) ? raw : raw?.products ?? [];
        setProducts(
          list
            .map((p): PaletteEntry | null => {
              const id = p?.id ?? p?.product_id;
              const name: string = p?.name ?? p?.product_name ?? '';
              if (id === undefined || id === null || !name) return null;
              const sku: string = p?.sku ?? '';
              return {
                key: `product:${id}`,
                primary: name,
                secondary: sku ? `SKU ${sku}` : undefined,
                tag: 'Product',
                icon: Inventory2Icon,
                path: `/products/edit/${id}`,
                searchText: `${name} ${sku}`.toLowerCase(),
              };
            })
            .filter((entry): entry is PaletteEntry => entry !== null)
        );
      }

      if (ordersResult.status === 'fulfilled') {
        setOrders(
          ordersResult.value.map((order) => ({
            key: `order:${order.order_id}`,
            primary: `#${order.order_number}`,
            secondary: order.customer_name,
            tag: 'Order',
            icon: ReceiptLongIcon,
            path: `/orders/${order.order_id}/edit`,
            searchText: `${order.order_number} ${order.customer_name ?? ''}`.toLowerCase(),
          }))
        );
      }

      setLoading(false);
    })();
  }, [open]);

  const groups = useMemo<PaletteGroup[]>(() => {
    const q = query.trim().toLowerCase();
    const build = (id: string, label: string, entries: PaletteEntry[]): PaletteGroup => {
      const matched = q ? entries.filter((entry) => entry.searchText.includes(q)) : entries;
      return {
        id,
        label,
        entries: matched.slice(0, GROUP_CAP),
        hiddenCount: Math.max(0, matched.length - GROUP_CAP),
      };
    };

    const next: PaletteGroup[] = [
      build('pages', 'Pages', PAGE_ENTRIES),
      build('settings', 'Settings', SETTINGS_ENTRIES),
    ];
    if (q) {
      next.push(build('products', 'Products', products));
      next.push(build('orders', 'Orders', orders));
    } else {
      // Empty query: pages + settings, plus a few recent orders once loaded.
      next.push({ id: 'orders', label: 'Recent Orders', entries: orders.slice(0, 3), hiddenCount: 0 });
    }
    return next.filter((group) => group.entries.length > 0);
  }, [query, products, orders]);

  const flat = useMemo(() => groups.flatMap((group) => group.entries), [groups]);
  const clampedIndex = flat.length > 0 ? Math.min(selectedIndex, flat.length - 1) : 0;

  // Reset the highlight whenever the query changes or the palette (re)opens.
  useEffect(() => {
    setSelectedIndex(0);
  }, [query, open]);

  // Keep the highlighted row visible while arrowing through a long list.
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-palette-index="${clampedIndex}"]`);
    if (el instanceof HTMLElement) el.scrollIntoView({ block: 'nearest' });
  }, [clampedIndex]);

  const handleClose = () => {
    setQuery('');
    onClose();
  };

  const handleSelect = (entry: PaletteEntry) => {
    if (entry.resetsOrders) resetUnread();
    navigate(entry.path);
    handleClose();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (flat.length > 0) setSelectedIndex((clampedIndex + 1) % flat.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (flat.length > 0) setSelectedIndex((clampedIndex - 1 + flat.length) % flat.length);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const entry = flat[clampedIndex];
      if (entry) handleSelect(entry);
    }
  };

  let rowIndex = -1;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      sx={{ '& .MuiDialog-container': { alignItems: 'flex-start' } }}
      PaperProps={{
        elevation: 8,
        sx: {
          mt: '10vh',
          borderRadius: 2.5,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          overflow: 'hidden',
        },
      }}
    >
      <Box onKeyDown={handleKeyDown}>
        {/* Search input */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 2, py: 1.5 }}>
          <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
          <InputBase
            autoFocus
            fullWidth
            placeholder="Search orders, products, pages…"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            inputProps={{ 'aria-label': 'Search orders, products, pages' }}
            sx={{ fontSize: '0.9375rem', color: 'text.primary' }}
          />
          {loading && (
            <CircularProgress size={16} sx={{ color: 'text.secondary', flexShrink: 0 }} />
          )}
        </Box>
        <Divider />

        {/* Results */}
        <Box ref={listRef} sx={{ maxHeight: '46vh', overflowY: 'auto', py: 0.5 }}>
          {flat.length === 0 ? (
            <Typography
              sx={{
                px: 2.5,
                py: 4,
                textAlign: 'center',
                fontSize: '0.875rem',
                color: 'text.secondary',
              }}
            >
              {loading ? 'Loading…' : `No results for “${query.trim()}”`}
            </Typography>
          ) : (
            groups.map((group) => (
              <Box key={group.id} sx={{ pb: 0.5 }}>
                <Typography
                  sx={{
                    px: 2.5,
                    pt: 1,
                    pb: 0.25,
                    fontSize: '0.6875rem',
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: 'text.secondary',
                  }}
                >
                  {group.label}
                </Typography>
                <List disablePadding>
                  {group.entries.map((entry) => {
                    rowIndex += 1;
                    const index = rowIndex;
                    const selected = index === clampedIndex;
                    const Icon = entry.icon;
                    return (
                      <ListItemButton
                        key={entry.key}
                        data-palette-index={index}
                        selected={selected}
                        onMouseEnter={() => setSelectedIndex(index)}
                        onClick={() => handleSelect(entry)}
                        sx={{
                          mx: 1,
                          my: 0.125,
                          px: 1.5,
                          py: 0.75,
                          borderRadius: 1.5,
                          gap: 1.25,
                          '&.Mui-selected': {
                            bgcolor: (theme: Theme) => alpha(theme.palette.primary.main, 0.1),
                            '&:hover': {
                              bgcolor: (theme: Theme) => alpha(theme.palette.primary.main, 0.14),
                            },
                          },
                        }}
                      >
                        <ListItemIcon
                          sx={{ minWidth: 0, color: selected ? 'primary.main' : 'text.secondary' }}
                        >
                          <Icon sx={{ fontSize: 18 }} />
                        </ListItemIcon>
                        <Box
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            display: 'flex',
                            alignItems: 'baseline',
                            gap: 1,
                          }}
                        >
                          <Typography
                            noWrap
                            sx={{ fontSize: '0.875rem', fontWeight: 500, color: 'text.primary' }}
                          >
                            {entry.primary}
                          </Typography>
                          {entry.secondary && (
                            <Typography noWrap sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                              {entry.secondary}
                            </Typography>
                          )}
                        </Box>
                        <Typography
                          sx={{
                            flexShrink: 0,
                            px: 0.75,
                            py: 0.1,
                            borderRadius: 0.75,
                            border: '1px solid',
                            borderColor: 'divider',
                            bgcolor: 'action.hover',
                            fontSize: '0.65rem',
                            fontWeight: 600,
                            letterSpacing: '0.04em',
                            textTransform: 'uppercase',
                            color: 'text.secondary',
                          }}
                        >
                          {entry.tag}
                        </Typography>
                      </ListItemButton>
                    );
                  })}
                </List>
                {group.hiddenCount > 0 && (
                  <Typography
                    sx={{
                      px: 2.5,
                      py: 0.25,
                      fontSize: '0.71875rem',
                      fontStyle: 'italic',
                      color: 'text.secondary',
                    }}
                  >
                    …{group.hiddenCount} more — keep typing to narrow
                  </Typography>
                )}
              </Box>
            ))
          )}
        </Box>

        {/* Footer hints */}
        <Divider />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 2, py: 1 }}>
          <Kbd>↑↓</Kbd>
          <Typography sx={{ fontSize: '0.71875rem', color: 'text.secondary' }}>navigate</Typography>
          <Typography sx={{ fontSize: '0.71875rem', color: 'text.secondary' }}>·</Typography>
          <Kbd>↵</Kbd>
          <Typography sx={{ fontSize: '0.71875rem', color: 'text.secondary' }}>open</Typography>
          <Typography sx={{ fontSize: '0.71875rem', color: 'text.secondary' }}>·</Typography>
          <Kbd>esc</Kbd>
          <Typography sx={{ fontSize: '0.71875rem', color: 'text.secondary' }}>close</Typography>
        </Box>
      </Box>
    </Dialog>
  );
};

export default CommandPalette;
